import { ChangeDetectionStrategy, Component, computed, inject, signal, Signal, WritableSignal } from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, DropdownButtonComponent, GroupButtonComponent, ModalService } from 'mbs-ui-kit';
import { BackupDestinationModalComponent } from 'mbs-ui-shared@components/backup-destination-modal/backup-destination-modal.component';
import { StorageDestinationsFacade } from 'mbs-ui-shared@facades/storage-destinations.facade';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import { CompanyDestination } from 'mbs-ui-shared@models/Company';
import { StorageDestinationWithLimit } from 'mbs-ui-shared@models/StorageAccount';
import { ADD_STORAGE_ACCOUNT_WIZARD_OPENER } from 'mbs-ui-shared@tokens/add-storage-account-wizard.token';
import { mapToCompanyDestinations } from 'mbs-ui-shared@utils/map-to-company-destinations.util';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import { catchError, filter, finalize, from, of, switchMap } from 'rxjs';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, DropdownButtonComponent, GroupButtonComponent, I18NextPipe, NgbDropdownModule, StorageTypeIconPipe],
  selector: 'mbs-company-destinations-step',
  standalone: true,
  templateUrl: './company-destinations-step.component.html'
})
export class CompanyDestinationsStepComponent {
  protected readonly ability = inject(AbilityService);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly modalService = inject(ModalService);
  private readonly storageDestinationsFacade = inject(StorageDestinationsFacade);
  private readonly openAddStorageAccountWizard = inject(ADD_STORAGE_ACCOUNT_WIZARD_OPENER);

  public readonly loading: WritableSignal<boolean> = signal(false);
  public readonly PermissionsEnum = PermissionsEnum;
  public readonly elementSelectors = {
    id: {
      addButton: 'company-add-destination-button',
      addDropdown: 'company-add-destination-dropdown',
      selectExistingButton: 'company-select-existing-destination-button'
    }
  };

  public readonly destinations: Signal<CompanyDestination[]> = computed(
    () => this.companyWizardFacade.company()?.inheritedDestinations ?? []
  );

  public selectExistingClickHandler(): void {
    if (this.loading()) return;

    this.loading.set(true);

    this.storageDestinationsFacade
      .load()
      .pipe(
        switchMap((all) => {
          const addedBucketIds = this.destinations().map(({ bucketId }) => bucketId);
          const modal = this.modalService.openRef(BackupDestinationModalComponent);

          modal.componentInstance.destinations = all.filter(({ id }) => !addedBucketIds.includes(id));
          modal.componentInstance.withLimits = true;
          modal.componentInstance.isLimitRequired = false;

          return from(modal.result as Promise<StorageDestinationWithLimit[]>);
        }),
        switchMap((selected) =>
          selected?.length ? this.companyWizardFacade.addDestinations(selected.map(mapToCompanyDestinations)) : of(null)
        ),
        catchError(() => of(null)),
        finalize(() => this.loading.set(false))
      )
      .subscribe();
  }

  public createDestinationClickHandler(): void {
    const company = this.companyWizardFacade.company();

    if (!company || this.loading()) return;

    // The opener loads the wizard's dictionary and storage types before the modal shows up, and resolves only once it closes
    this.loading.set(true);

    from(this.openAddStorageAccountWizard({ lockedCompany: company }))
      .pipe(
        filter(Boolean),
        switchMap(() => this.companyWizardFacade.reloadCompany()),
        catchError(() => of(null)),
        finalize(() => this.loading.set(false))
      )
      .subscribe();
  }
}
